import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import seaChatbotImg from '@/assets/all-icons/sea-chatbot.PNG';
import SendIcon from '@/assets/all-icons/send-icon';
import config from '@/config';
import ProfileDataContext from '@/context/ProfileDataContext';
import useTranslations from '@/i18n/useTranslations';
import fetchWrapper from '@/services/fetchWrapper.service';
import { getPreferredLocale } from '@/services/locale.service';
import AskAiQuotaNotice from '@/shared-components/ai/components/AskAiQuotaNotice';
import useAskAiEntitlement from '@/shared-components/ai/useAskAiEntitlement';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import SuartePlusPopup from '@/shared-components/popups/components/SuartePlusPopup';
import Text from '@/shared-components/text/components/Text';
import { resolveAiAccessErrorMessage } from '@/utils/aiAccessErrors';

import AiRecommendArtistCard from './AiRecommendArtistCard';

import './styles/ProfileAskAi.css';
import '@/shared-components/ai/styles/AiConversationComposer.css';

const MAX_QUESTION_LENGTH = 400;
const REVEAL_INTERVAL_MS = 24;
const REVEAL_MIN_CHARS = 2;
const REVEAL_MAX_CHARS = 9;

const formatMessageTime = (timestamp, locale) => {
  if (!timestamp) return null;

  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
  });
};

export default function ProfileAskAi({
  onClose,
  assistantName = 'SEA',
  mode = 'artist',
  targetId = null,
}) {
  const navigate = useNavigate();
  const t = useTranslations('common.profile.ask_ai');
  const locale = getPreferredLocale();
  const { profileData, profileAiMessages, setProfileAiMessages } = useContext(ProfileDataContext);
  const [question, setQuestion] = useState('');
  const [localMessages, setLocalMessages] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const {
    clearQuotaMessage,
    handleAccessError,
    quotaMessage,
    setShowSuartePlusPopup,
    showSuartePlusPopup,
  } = useAskAiEntitlement();
  const conversationRef = useRef(null);
  const conversationKeyRef = useRef(null);
  const textareaRef = useRef(null);
  const resolvedTargetId = targetId || profileData?._id || profileData?.user_id || null;
  const isGalleryMode = mode === 'gallery';

  const hasSharedMessageState =
    Array.isArray(profileAiMessages)
    && typeof setProfileAiMessages === 'function';
  const messages = hasSharedMessageState ? profileAiMessages : localMessages;
  const setMessages = hasSharedMessageState ? setProfileAiMessages : setLocalMessages;

  const welcomeName = useMemo(() => {
    if (typeof assistantName === 'string' && assistantName.trim()) {
      return assistantName.trim();
    }
    return 'SEA';
  }, [assistantName]);

  const activeRevealMessageId = useMemo(() => {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const message = messages[index];
      if (
        message?.role === 'assistant'
        && message?.isRevealing === true
        && typeof message?.fullContent === 'string'
      ) {
        return message.id;
      }
    }
    return null;
  }, [messages]);

  useEffect(() => {
    const nextKey = `${mode}:${resolvedTargetId || 'none'}`;
    if (conversationKeyRef.current === nextKey) {
      return;
    }
    conversationKeyRef.current = nextKey;
    setMessages([]);
    setQuestion('');
    setError(null);
    clearQuotaMessage();
  }, [mode, resolvedTargetId, setMessages]);

  useEffect(() => {
    if (messages.length > 0) {
      return;
    }

    setMessages([
      {
        id: `profile-ai-welcome-${mode}-${resolvedTargetId ?? 'default'}`,
        role: 'assistant',
        content: isGalleryMode
          ? t('messages.welcome_gallery', { assistantName: welcomeName })
          : t('messages.welcome_artist', { assistantName: welcomeName }),
        timestamp: new Date(),
        hideMeta: true,
      },
    ]);
    setQuestion('');
    setError(null);
    clearQuotaMessage();
  }, [isGalleryMode, messages.length, mode, resolvedTargetId, setMessages, welcomeName]);

  useEffect(() => {
    if (!conversationRef.current) return;
    conversationRef.current.scrollTop = conversationRef.current.scrollHeight;
  }, [isSubmitting, messages]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = 'auto';
    const lineHeight = Number.parseFloat(window.getComputedStyle(textarea).lineHeight) || 20;
    const maxHeight = lineHeight * 3;
    textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, [question]);

  useEffect(() => {
    if (!activeRevealMessageId) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setMessages((prev) => {
        const messageIndex = prev.findIndex((message) => message.id === activeRevealMessageId);
        if (messageIndex < 0) {
          return prev;
        }

        const message = prev[messageIndex];
        if (!message?.isRevealing || typeof message?.fullContent !== 'string') {
          return prev;
        }

        const fullContent = message.fullContent;
        const currentLength = typeof message.content === 'string' ? message.content.length : 0;
        if (currentLength >= fullContent.length) {
          const finalizedMessages = [...prev];
          finalizedMessages[messageIndex] = {
            ...message,
            content: fullContent,
            isRevealing: false,
            fullContent: undefined,
          };
          return finalizedMessages;
        }

        const remaining = fullContent.length - currentLength;
        const chunkSize = Math.min(
          remaining,
          Math.max(REVEAL_MIN_CHARS, Math.min(REVEAL_MAX_CHARS, Math.ceil(remaining / 10))),
        );
        const nextContent = fullContent.slice(0, currentLength + chunkSize);
        const done = nextContent.length >= fullContent.length;
        const nextMessages = [...prev];
        nextMessages[messageIndex] = {
          ...message,
          content: nextContent,
          isRevealing: !done,
          fullContent: done ? undefined : fullContent,
        };
        return nextMessages;
      });
    }, REVEAL_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [activeRevealMessageId, setMessages]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting || !resolvedTargetId) {
      return;
    }

    const trimmedQuestion = question.trim();
    if (!trimmedQuestion) {
      return;
    }

    const userMessage = {
      id: `profile-user-${Date.now()}`,
      role: 'user',
      content: trimmedQuestion,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setQuestion('');
    setError(null);
    clearQuotaMessage();
    setIsSubmitting(true);

    try {
      const endpoint = isGalleryMode
        ? `${config.apis.api.url}/ai/galleries/${resolvedTargetId}/ask`
        : `${config.apis.api.url}/ai/users/${resolvedTargetId}/ask`;
      const { response, data } = await fetchWrapper.post(
        endpoint,
        {
          optionalToken: true,
          includeCredentials: true,
          body: {
            question: trimmedQuestion,
          },
        },
      );

      if (!response.ok) {
        if (handleAccessError(data, t)) {
          return;
        }
        throw new Error(resolveAiAccessErrorMessage(data, t));
      }

      const answer = typeof data?.answer === 'string' ? data.answer.trim() : '';
      if (!answer) {
        throw new Error(t('errors.empty_answer'));
      }

      setMessages((prev) => [
        ...prev,
        {
          id: `profile-assistant-${Date.now()}`,
          role: 'assistant',
          content: '',
          timestamp: new Date(),
          fullContent: answer,
          isRevealing: true,
          recommendedArtists: Array.isArray(data?.recommended_artists)
            ? data.recommended_artists.filter(Boolean)
            : [],
        },
      ]);
    } catch (submitError) {
      const message =
        submitError instanceof Error && submitError.message
          ? submitError.message
          : t('errors.unable_to_answer');
      setError(message);
      setMessages((prev) => [
        ...prev,
        {
          id: `profile-assistant-error-${Date.now()}`,
          role: 'assistant',
          content: t('messages.retry_later'),
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="profile-view-ai__container">
      <div className="sea-widget__panel-header element-non-selectable">
        <img
          src={seaChatbotImg}
          alt={t('title')}
          className="sea-widget__panel-avatar"
        />
        <div className="sea-widget__panel-title">
          <div className="sea-widget__title">{t('title')}</div>
        </div>
        <div className="sea-widget__close-container">
          <XIcon
            className="sea-widget__close"
            onClick={onClose}
          />
        </div>
      </div>
      <div className="profile-view-ai__main">
        <div className="profile-view-ai__messages remove-scrollbar" ref={conversationRef}>
          {messages.map((message) => {
            const messageTime = message.hideMeta
              ? null
              : formatMessageTime(message.timestamp, locale);

            return (
              <div
                key={message.id}
                className={`profile-view-ai__message profile-view-ai__message--${message.role}${message.isRevealing ? ' profile-view-ai__message--revealing' : ''}`}
              >
                <Text className="profile-view-ai__message-text" paragraph >
                  {message.content}
                </Text>
                {message.role === 'assistant' && (message.recommendedArtists?.length ?? 0) > 0 ? (
                  <div className="profile-view-ai__recommendations">
                    {message.recommendedArtists.map((artist) => {
                      const username = artist?.user_username?.trim?.() || '';
                      const interactive = Boolean(username);

                      return artist?._id ? (
                        <AiRecommendArtistCard
                          key={artist._id}
                          artist={artist}
                          interactive={interactive}
                          onClick={interactive ? () => navigate(`/user/${username}`, { state: { from: true } }) : undefined}
                        />
                      ) : null;
                    })}
                  </div>
                ) : null}
                {messageTime ? (
                  <span className="profile-view-ai__message-time">{messageTime}</span>
                ) : null}
              </div>
            );
          })}

          {isSubmitting ? (
            <div className="profile-view-ai__typing" role="status" aria-live="polite">
              <span />
              <span />
              <span />
            </div>
          ) : null}
          <AskAiQuotaNotice
            message={quotaMessage}
            onSuartePlusClick={() => setShowSuartePlusPopup(true)}
          />
        </div>

        <form className="profile-view-ai__form ai-conversation__composer" onSubmit={handleSubmit}>
          <textarea
            ref={textareaRef}
            className="profile-view-ai__textarea ai-conversation__composer-textarea remove-scrollbar"
            placeholder={isGalleryMode ? t('placeholders.gallery') : t('placeholders.artist')}
            value={question}
            rows={1}
            maxLength={MAX_QUESTION_LENGTH}
            onChange={(event) => setQuestion(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                event.currentTarget.form?.requestSubmit();
              }
            }}
            disabled={!resolvedTargetId || isSubmitting}
          />
          <button
            type="submit"
            className="profile-view-ai__submit ai-conversation__composer-submit"
            disabled={isSubmitting || !resolvedTargetId || !question.trim()}
            aria-label={t('actions.ask')}
            title={t('actions.ask')}
          >
            <SendIcon className="ai-conversation__composer-submit-icon" />
          </button>
        </form>

        {error ? (
          <Text className="profile-view-ai__error font-size-small" paragraph>
            {error}
          </Text>
        ) : null}
      </div>
      <SuartePlusPopup
        open={showSuartePlusPopup}
        onClose={() => setShowSuartePlusPopup(false)}
        highlightedBenefit="ask_ai"
      />
    </div>
  );
}
